import type { Extension, Resource } from '@medplum/fhirtypes';

import { ELUVE_FHIR_NAMESPACE } from '@eluve/fhir-utils';

/**
 * Cross-agent markers stamped on every AI-proposed resource at creation —
 * single declarations shared by backend agents and frontend review surfaces
 * (ADR 0002/0003). Agent-owned systems (clinical-task-id, care-plan-id, …)
 * do NOT live here; they belong to their owning agent/domain lib.
 */

/** meta.tag recording the granting user the agent acted for. */
export const ON_BEHALF_OF_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/on-behalf-of-user`;

/**
 * meta.tag naming the agent that authored a resource (Coding.code = agent id).
 * A `meta.tag` Coding.system, NOT an Identifier.system, so it carries no `-id`
 * suffix and is not built via `buildIdentifierSystem`.
 *
 * The uniform machine-authorship marker: review surfaces read MANUAL authorship
 * as its absence, so every machine-written reviewable resource must carry it.
 */
export const ELUVE_AGENT_TAG_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/agent`;

/** Whether an agent authored the resource — independent of which agent, and of
 *  where the resource sits in review. */
export function hasAgentTag(resource: Resource): boolean {
  return (resource.meta?.tag ?? []).some(
    (tag) => tag.system === ELUVE_AGENT_TAG_SYSTEM,
  );
}

/** Stamp the machine-authorship marker. Replaces any agent tag already present
 *  so re-writes cannot stack them; every other tag survives. */
export function withAgentTag<T extends Resource>(
  resource: T,
  agentId: string,
): T {
  const others = (resource.meta?.tag ?? []).filter(
    (tag) => tag.system !== ELUVE_AGENT_TAG_SYSTEM,
  );
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [
        ...others,
        { system: ELUVE_AGENT_TAG_SYSTEM, code: agentId, display: agentId },
      ],
    },
  };
}

/**
 * Ties a finding back to the session it was extracted from (value =
 * appointmentId). Queryable identifier; doubles as the supersede/idempotency
 * key when a session is re-extracted.
 */
export const DERIVED_FROM_APPOINTMENT_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/derived-from-appointment`;

/**
 * Extension carrying the agent's self-reported confidence (0–1) in an
 * extracted finding. FHIR has no native confidence field, so it rides an
 * Eluve-namespaced extension; review surfaces sort most- to least-confident.
 */
export const FINDING_CONFIDENCE_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/finding-confidence`;

/** Complex extension carrying the evidence quote's located transcript span:
 *  `start`/`end` (valueInteger, half-open UTF-16 offsets) + `text`
 *  (valueString, the transcript's own text for that range). Additive beside
 *  the human-readable `note[0].text` evidence, never a replacement. */
export const FINDING_SOURCE_SPAN_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/finding-source-span`;

/**
 * Half-open [start, end) span in UTF-16 code units, with the transcript's own
 * text for that range — the one span shape every producer and reader shares:
 * the agent's quote locator, the extension above, the transport contracts, and
 * the review surfaces' citation views.
 */
export interface SourceSpan {
  readonly start: number;
  readonly end: number;
  /** The transcript's own text for the range — may differ from the model's
   *  quote in whitespace, since quotes are located tolerantly. */
  readonly text: string;
}

/**
 * Extension carrying when a finding was created (valueDateTime), for resource
 * types FHIR gives no creation instant: Condition has `recordedDate`,
 * Observation `issued` and ServiceRequest `authoredOn`, but Goal has only
 * `statusDate` — a `date`, too coarse to order two goals from one visit. Review
 * surfaces order oldest-first off this, so a newly added row lands last.
 *
 * Distinct from `meta.lastUpdated`, which moves on every edit and would sink an
 * edited row to the bottom.
 */
export const FINDING_CREATED_AT_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/finding-created-at`;

/** The creation instant stamped on a finding's extension, or null. */
export function readCreatedAt(resource: Resource): string | null {
  return (
    (resource as { extension?: Extension[] }).extension?.find(
      (e) => e.url === FINDING_CREATED_AT_EXTENSION,
    )?.valueDateTime ?? null
  );
}

/** Stamp the creation instant on a resource that has no native field for it. */
export function withCreatedAt<T extends Resource>(
  resource: T,
  createdAt: string,
): T {
  return {
    ...resource,
    extension: [
      ...((resource as { extension?: Extension[] }).extension ?? []),
      { url: FINDING_CREATED_AT_EXTENSION, valueDateTime: createdAt },
    ],
  };
}

/** The 0–1 confidence stamped on a finding's extension, or null. */
export function readConfidence(resource: Resource): number | null {
  const extension = (resource as { extension?: Extension[] }).extension?.find(
    (e) => e.url === FINDING_CONFIDENCE_EXTENSION,
  );
  return extension?.valueDecimal ?? null;
}

/** The finding-source-span complex extension for a located span. Appended to a
 *  resource's `extension` beside the human-readable evidence, never a
 *  replacement for it. */
export function buildSourceSpanExtension(span: SourceSpan): Extension {
  return {
    url: FINDING_SOURCE_SPAN_EXTENSION,
    extension: [
      { url: 'start', valueInteger: span.start },
      { url: 'end', valueInteger: span.end },
      { url: 'text', valueString: span.text },
    ],
  };
}

export function readSourceSpan(resource: Resource): SourceSpan | null {
  const ext = (resource as { extension?: Extension[] }).extension?.find(
    (e) => e.url === FINDING_SOURCE_SPAN_EXTENSION,
  );
  if (!ext?.extension) return null;
  const sub = (url: string) => ext.extension?.find((e) => e.url === url);
  const start = sub('start')?.valueInteger;
  const end = sub('end')?.valueInteger;
  const text = sub('text')?.valueString;
  if (start === undefined || end === undefined || text === undefined) {
    return null;
  }
  return { start, end, text };
}
